import type { ProductNodeBuilderProps } from 'components/common-form/builder/form/node/ProductNodeBuilder.model';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import type { GridData, GridMetadata } from 'models/forms/fields';
import { FC, useMemo } from 'react';
import type { QuoteWorkspaceVersion } from 'state/userPreferences/userPreferences.model';

import { GridWithoutPagination } from './GridWithoutPagination';

const empty: readonly [] = [];

function filterHiddenColumns<TColumn extends { isForAdvancedMode?: boolean }>(
  columns: readonly TColumn[] | undefined,
  version: QuoteWorkspaceVersion,
  mustForceAdvancedMode: boolean,
): readonly TColumn[] {
  if (version === 'Advanced' || mustForceAdvancedMode === true) {
    return columns ?? empty;
  }
  return columns?.filter(c => c.isForAdvancedMode !== true) ?? empty;
}

type FilterableColumn = { isForAdvancedMode?: boolean };

export function useFilterVisibleHeaders<TColumn extends FilterableColumn>(
  unfilteredHeaders: TColumn[],
  quoteWorkspaceVersion: QuoteWorkspaceVersion,
  mustForceAdvancedMode: boolean,
) {
  return useMemo(
    () => filterHiddenColumns(unfilteredHeaders, quoteWorkspaceVersion, mustForceAdvancedMode),
    [unfilteredHeaders, quoteWorkspaceVersion, mustForceAdvancedMode],
  );
}
export const GridField: FC<ProductNodeBuilderProps> = ({
  node: { id },
  quoteWorkspaceVersion,
  mustForceAdvancedMode,
  tabIndex,
}) => {
  const { headers: unfilteredHeaders, valuesMetadata, hasRowIndexColumn } = useFieldMetadata<GridMetadata>(id)!;
  const headers = useFilterVisibleHeaders(unfilteredHeaders, quoteWorkspaceVersion, mustForceAdvancedMode);
  const nbColumns = headers?.length ?? 0;
  const [gridData = []] = useData<GridData>(id);
  const nbRows = gridData.length;

  if (headers === undefined || nbRows === 0 || nbColumns === 0) {
    return null;
  }

  return (
    <GridWithoutPagination
      id={id}
      headers={headers}
      rowsMetadata={valuesMetadata}
      hasRowIndexColumn={hasRowIndexColumn}
      nbColumns={nbColumns}
      tabIndex={tabIndex}
    />
  );
};

GridField.displayName = 'GridField';
